// Site-wide SEO constants for the public marketing site (photohawk.com): the canonical origin, the
// languages a page can be indexed in, and the 1200x630 link-preview images in public/images/og
// (generated by scripts/brand-assets/generate.sh).

/** The canonical origin. The apex is canonical; www.photohawk.com is an alias that redirects here. */
export const PROD_ORIGIN = "https://photohawk.com";
export const STAGING_ORIGIN = "https://photohawk-staging.com";

/**
 * The marketing origin for a request host. Staging pages point at staging so its previews and
 * sitemap can be checked before a prod deploy; everything else is canonical on the prod apex.
 */
export const siteOrigin = (host: string | undefined): string => {
  const h = (host || "").toLowerCase();
  return h === "photohawk-staging.com" || h.endsWith(".photohawk-staging.com") ? STAGING_ORIGIN : PROD_ORIGIN;
};

/**
 * Languages a localised page can be offered (and indexed) in; each page lists its own (pages.ts). English is the
 * default and x-default. en/es/pt are the site's long-standing page languages; de, it, ja, ms and zh are also
 * site UI languages (src/i18n); fr, nl, no and th are workspace profile languages that the site UI doesn't
 * have yet, so on those pages only the page body is translated and the nav and footer stay in English (i18next's
 * fallbackLng).
 */
export const SEO_LANGS = ["en", "es", "pt", "de", "fr", "it", "nl", "no", "ja", "zh", "ms", "th"] as const;
export type SeoLang = (typeof SEO_LANGS)[number];

/** The query parameter that selects a page's language (?lang=es). English has no parameter. */
export const LANG_PARAM = "lang";

export const OG_LOCALE: Record<SeoLang, string> = {
  en: "en_GB",
  es: "es_ES",
  pt: "pt_PT",
  de: "de_DE",
  fr: "fr_FR",
  it: "it_IT",
  nl: "nl_NL",
  no: "nb_NO",
  ja: "ja_JP",
  zh: "zh_CN",
  ms: "ms_MY",
  th: "th_TH",
};

/** The BCP 47 tag for a page language, for hreflang and <html lang>: the ?lang= value, except Simplified
 *  Chinese (zh-Hans) and Norwegian Bokmål (nb), which are more precise than the bare codes. */
export const LANG_TAG: Record<SeoLang, string> = Object.fromEntries(
  SEO_LANGS.map((l) => [l, l === "zh" ? "zh-Hans" : l === "no" ? "nb" : l]),
) as Record<SeoLang, string>;

/** A language code (?lang=, i18next or a browser's "nb-NO") to its base page language: lower case, no region,
 *  and Norwegian Bokmål / Nynorsk ("nb" / "nn") as "no", the code workspace profiles use. */
export const baseLang = (lang: string | null | undefined): string => {
  const base = (lang || "en").toLowerCase().split("-")[0];
  return base === "nb" || base === "nn" ? "no" : base;
};

/** A page's URL in a language: the bare path in English, `?lang=xx` otherwise. `params`: a listing's own query
 *  parameters (PublicPage.canonicalParams, e.g. ?country=gb), before the language. */
export const localisedUrl = (origin: string, path: string, lang: SeoLang, params: [string, string][] = []): string => {
  const parts = [...params.map(([k, v]) => `${k}=${encodeURIComponent(v)}`), ...(lang === "en" ? [] : [`${LANG_PARAM}=${lang}`])];
  return `${origin}${path}${parts.length ? `?${parts.join("&")}` : ""}`;
};

/** The language a URL's query string asks for, if it's one of `langs`. */
export const langFromSearch = (search: string, langs: readonly SeoLang[] = SEO_LANGS): SeoLang | null => {
  const raw = new URLSearchParams(search).get(LANG_PARAM);
  const v = raw ? baseLang(raw) : null;
  return v && (langs as readonly string[]).includes(v) ? (v as SeoLang) : null;
};

export interface OgImage {
  /** Path under the site origin. */
  path: string;
  width: number;
  height: number;
  type: string;
  alt: string;
}

const og = (name: string, alt: string): OgImage => ({
  path: `/images/og/${name}.jpg`,
  width: 1200,
  height: 630,
  type: "image/jpeg",
  alt,
});

/** Link-preview images. The alt text repeats the image's headline (see generate.sh). */
export const OG_IMAGES = {
  default: og("default", "Photohawk: share and sell your event photos, found by face or bib."),
  webUploader: og("web-uploader", "The new Photohawk web uploader. Faster than FTP."),
  previewFirst: og("preview-first-uploads", "Photohawk preview-first uploads: your gallery is live in minutes, not hours."),
  pricing: og("pricing", "Photohawk plans and pricing: share for free, or sell."),
  pricingChanges: og("pricing-changes-2026", "Photohawk pricing is changing on 1 November 2026."),
  onDemandFtp: og("on-demand-ftp", "Photohawk on-demand FTP: your own FTP server."),
  previewProtection: {
    path: "/images/demo/preview-protection/preview.jpg",
    width: 1200,
    height: 675,
    type: "image/jpeg",
    alt: "A Photohawk gallery preview with people softly blurred and faces kept clear.",
  },
} satisfies Record<string, OgImage>;

/** Photohawk's terms of service (the footer's "Terms of Service" link). */
export const TERMS_URL =
  "https://app.termly.io/policy-viewer/policy.html?policyUUID=19d74052-fc1f-4cf3-8445-01a26252455b";

/** The help centre (Intercom, docs.photohawk.com). Its "/" 302s to "/en/", so link straight to "/en/". */
export const HELP_URL = "https://docs.photohawk.com/en/";

/** Logo for structured data (square, solid background). */
export const LOGO_PATH = "/icon-512.png";

/** Photohawk's own social profiles (the footer links), for Organization.sameAs. */
export const SOCIAL_PROFILES = [
  "https://facebook.com/thephotohawk",
  "https://instagram.com/photohawk.io",
  "https://www.linkedin.com/company/photohawk",
];
